import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
    Box,
    IconButton,
    InputBase,
    Paper,
    Popper,
    ClickAwayListener,
    List,
    ListItemButton,
    ListItemText,
    Divider,
    Typography,
    CircularProgress,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { iconButtonSx } from '../../UI/iconButtonSx';
import { useSearchProducts } from '../../../hooks/storefront/useSearchProducts';
import { formatMoney } from '../../../utils/money';

const DEBOUNCE_MS = 300;
const MAX_RESULTS = 5;

const ExpandableSearch = () => {
    const { t, i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [debounced, setDebounced] = useState('');
    const [highlightIdx, setHighlightIdx] = useState(-1);
    const inputRef = useRef();
    const wrapperRef = useRef();

    // Debounce typing → autocomplete trigger.
    useEffect(() => {
        const id = setTimeout(() => setDebounced(query), DEBOUNCE_MS);
        return () => clearTimeout(id);
    }, [query]);

    const { searchResults = [], isLoading } = useSearchProducts(
        debounced ? { q: debounced, per_page: MAX_RESULTS } : { skip: true }
    );

    const showSuggestions = open && debounced.trim().length > 0;

    const items = useMemo(() => searchResults.slice(0, MAX_RESULTS), [searchResults]);

    const goToResults = () => {
        const q = query.trim();
        if (!q) return;
        navigate(`/search?q=${encodeURIComponent(q)}`);
        setOpen(false);
    };

    const goToProduct = (product) => {
        if (!product?.slug) return;
        navigate(`/products/${product.slug}`);
        setOpen(false);
    };

    const handleKeyDown = (e) => {
        if (!showSuggestions) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlightIdx((i) => Math.min(items.length, i + 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlightIdx((i) => Math.max(-1, i - 1));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (highlightIdx >= 0 && highlightIdx < items.length) {
                goToProduct(items[highlightIdx]);
            } else {
                goToResults();
            }
        } else if (e.key === 'Escape') {
            setOpen(false);
        }
    };

    return (
        <Box sx={{ position: 'relative' }} ref={wrapperRef}>
            {!open ? (
                <IconButton sx={iconButtonSx} onClick={() => setOpen(true)}>
                    <Search />
                </IconButton>
            ) : (
                <ClickAwayListener onClickAway={() => setOpen(false)}>
                    <Box>
                        <Paper
                            component="form"
                            onSubmit={(e) => { e.preventDefault(); goToResults(); }}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                px: 1,
                                width: 260,
                                height: 36,
                                border: (t) => `1px solid ${t.palette.divider}`,
                                bgcolor: 'background.paper',
                            }}
                        >
                            <Search fontSize="small" sx={{ color: 'text.secondary' }} />
                            <InputBase
                                inputRef={inputRef}
                                autoFocus
                                placeholder={t('navigation.search')}
                                value={query}
                                onChange={(e) => {
                                    setQuery(e.target.value);
                                    setHighlightIdx(-1);
                                }}
                                onKeyDown={handleKeyDown}
                                sx={{ ml: 1, flex: 1, color: 'text.primary' }}
                            />
                            {isLoading && <CircularProgress size={14} thickness={5} />}
                        </Paper>

                        <Popper
                            open={showSuggestions}
                            anchorEl={wrapperRef.current}
                            placement={isArabic ? 'bottom-end' : 'bottom-start'}
                            sx={{ zIndex: (t) => t.zIndex.modal + 2 }}
                        >
                            <Paper
                                elevation={4}
                                sx={{ mt: 0.5, width: 360, maxWidth: 'calc(100vw - 24px)', borderRadius: 2, overflow: 'hidden' }}
                            >
                                {items.length === 0 && !isLoading ? (
                                    <Box sx={{ p: 2, textAlign: 'center' }}>
                                        <Typography variant="caption" color="text.secondary">
                                            {t('search.noSuggestions', 'No matches')}
                                        </Typography>
                                    </Box>
                                ) : (
                                    <List dense disablePadding>
                                        {items.map((p, idx) => {
                                            const name = p.name?.[isArabic ? 'ar' : 'en']
                                                || p.name?.en
                                                || p.name
                                                || '';
                                            return (
                                                <ListItemButton
                                                    key={p.id || p.slug}
                                                    selected={idx === highlightIdx}
                                                    onMouseEnter={() => setHighlightIdx(idx)}
                                                    onClick={() => goToProduct(p)}
                                                >
                                                    {(p.thumb_url || p.image) && (
                                                        <Box
                                                            component="img"
                                                            src={p.thumb_url || p.image}
                                                            alt=""
                                                            sx={{
                                                                width: 36,
                                                                height: 36,
                                                                borderRadius: 1,
                                                                objectFit: 'cover',
                                                                me: 1.5,
                                                                flexShrink: 0,
                                                            }}
                                                        />
                                                    )}
                                                    <ListItemText
                                                        primary={name}
                                                        secondary={
                                                            p.price != null
                                                                ? formatMoney(p.price, p.currency)
                                                                : undefined
                                                        }
                                                        primaryTypographyProps={{
                                                            variant: 'body2',
                                                            noWrap: true,
                                                            fontWeight: 600,
                                                        }}
                                                        secondaryTypographyProps={{ variant: 'caption' }}
                                                    />
                                                </ListItemButton>
                                            );
                                        })}
                                        <Divider />
                                        <ListItemButton onClick={goToResults} selected={highlightIdx === items.length}>
                                            <ListItemText
                                                primary={t('search.seeAll', { defaultValue: "See all results for '{{q}}'", q: query })}
                                                primaryTypographyProps={{
                                                    variant: 'body2',
                                                    color: 'primary.main',
                                                    fontWeight: 700,
                                                    noWrap: true,
                                                }}
                                            />
                                        </ListItemButton>
                                    </List>
                                )}
                            </Paper>
                        </Popper>
                    </Box>
                </ClickAwayListener>
            )}
        </Box>
    );
};

export default ExpandableSearch;
